/**
 * Centralized, validated app configuration.
 *
 * Reads ALL VITE_* env vars in one place and fails fast at startup
 * if any required var is missing. No other file should read import.meta.env
 * directly — import from here instead.
 */

// ── Helpers ─────────────────────────────────────────────────────────

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `[app-config] Missing required env var: ${name}. ` +
      `Add it to your .env file or CI environment.`
    );
  }
  return value;
}

function optional(value: string | undefined, fallback: string = ''): string {
  return value || fallback;
}

// ── Config Object ───────────────────────────────────────────────────

export const appConfig = {
  /**
   * Commercetools (browser-side SDK — read-only, public scope).
   */
  ct: {
    projectKey: required('VITE_PROJECT_KEY', import.meta.env.VITE_PROJECT_KEY),
    apiUrl: required('VITE_API_URL', import.meta.env.VITE_API_URL),
    clientId: required('VITE_CLIENT_ID', import.meta.env.VITE_CLIENT_ID),
  },

  /** Backend API (ecom-backend-middleware) */
  backend: {
    /** Primary backend URL — falls back to localhost in development */
    baseUrl: optional(import.meta.env.VITE_BACKEND_API_URL) || 'http://localhost:3002',
    /** Public endpoints that skip auth headers */
    publicEndpoints: [
      '/api/products',
      '/api/product-projections',
      '/api/product-selections',
      '/api/categories',
      '/api/stores',
      '/api/product-discounts',
      '/api/cart-discounts',
      '/api/customer-groups',
      '/api/project'
    ],
  },

  /** Application defaults */
  defaults: {
    locale: 'en',
    currency: 'USD',
    country: 'US',
    storeKey: 'informa_market_us',
    eventKey: 'sfr_west_2026',
  },
} as const;

export type AppConfig = typeof appConfig;
export default appConfig;
